import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CommonSelect from "../../components/common/CommonSelect";
import { capitalizeFirstLetter } from "../../utils/capitalize";
import {
  checkIsFormless,
  isDeclinedOutcome,
  isEnrolledOutcome,
  isProgramCompletionOutcome,
} from "../../api/assessment";

const normalizeFormType = (val) => {
  if (!val) return "";
  const s = String(val).toUpperCase().trim();
  if (s === "FOLLOWUP" || s === "FOLLOW_UP" || s === "FOLLOW-UP")
    return "FOLLOW_UP";
  if (s === "INITIAL") return "INITIAL";
  if (s === "DISCHARGE") return "DISCHARGE";
  return s;
};

const lookupProgValue = (map, prog, canonicalId) => {
  if (!map) return "";
  if (map[prog?.id]) return map[prog.id];
  if (map[canonicalId]) return map[canonicalId];
  if (prog?.programId && map[prog.programId]) return map[prog.programId];
  if (prog?.name && map[prog.name]) return map[prog.name];
  return "";
};

const EnrolledProgram = ({
  prog,
  readOnly = false,
  primaryColor = "#403C6F",
  borderColor = "#E8E8E9",
  onRemoveProgram,
  onAssessmentTypeChange,
  selectedOutcome = {},
  setSelectedOutcome,
  selectedSubReason = {},
  setSelectedSubReason,
  declinedReason = {},
  setDeclinedReason,
  declinedNotes = {},
  setDeclinedNotes,
  referenceData = null,
  outcomesList = [],
  formlessOutcomeReasons = {},
  dynamicOutcomeReasons = {},
  getOutcomeDisplayName = (val) => val,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const selectRef = useRef(null);
  const assessmentSelectRef = useRef(null);

  const progNameStr = String(
    prog?.name || prog?.programDisplayName || prog?.displayName || "",
  ).toUpperCase();
  const progIdStr = String(prog?.id || prog?.programId || "").toUpperCase();
  const isBiosimilar = Boolean(
    progNameStr.includes("BIOSIMILAR") || progIdStr.includes("BIOSIMILAR"),
  );

  const isFormless = Boolean(
    prog?.isFormless || checkIsFormless(prog, referenceData),
  );

  const progId = String(prog?.id || "").trim();
  const canonicalId = progId.toUpperCase();

  // Consistent value lookup for this program
  const getProgValue = (map) => lookupProgValue(map, prog, canonicalId);

  const progOutcome = getProgValue(selectedOutcome);
  const progSubReason = getProgValue(selectedSubReason);
  const progReason = getProgValue(declinedReason);
  const progNotes = getProgValue(declinedNotes);

  const isDeclined = isDeclinedOutcome(progOutcome, referenceData);
  const isEnrolled = isEnrolledOutcome(progOutcome, referenceData);
  const isProgramCompletion = isProgramCompletionOutcome(
    progOutcome,
    referenceData,
  );
  const isAssessmentTypeDisabled = Boolean(
    readOnly || !onAssessmentTypeChange || isProgramCompletion,
  );

  const defaultAssessmentType =
    normalizeFormType(
      prog?.defaultAssessmentType ||
        prog?.initialFormType ||
        prog?.formType ||
        prog?.assessmentType,
    ) || "INITIAL";

  const handleOutcomeChangeAssessmentType = (outcomeName) => {
    if (!onAssessmentTypeChange) return;
    const progKey = prog?.id || prog?.programId || canonicalId;
    if (isProgramCompletionOutcome(outcomeName, referenceData)) {
      onAssessmentTypeChange(progKey, "DISCHARGE");
    } else {
      onAssessmentTypeChange(progKey, defaultAssessmentType);
    }
  };

  const prevOutcomeRef = React.useRef(progOutcome);

  React.useEffect(() => {
    if (!onAssessmentTypeChange || isBiosimilar) return;
    const progKey = prog?.id || prog?.programId || canonicalId;
    const currentProgType = normalizeFormType(
      prog?.formType || prog?.assessmentType || "",
    );

    const outcomeChanged = prevOutcomeRef.current !== progOutcome;
    if (outcomeChanged) {
      prevOutcomeRef.current = progOutcome;
    }
    const needsDefaultType = outcomeChanged
      ? currentProgType !== defaultAssessmentType
      : !currentProgType;

    if (isProgramCompletion) {
      if (currentProgType !== "DISCHARGE") {
        onAssessmentTypeChange(progKey, "DISCHARGE");
      }
    } else if (needsDefaultType) {
      onAssessmentTypeChange(progKey, defaultAssessmentType);
    }
  }, [
    isBiosimilar,
    isProgramCompletion,
    isEnrolled,
    progOutcome,
    prog?.formType,
    prog?.assessmentType,
    prog?.id,
    prog?.programId,
    canonicalId,
    defaultAssessmentType,
    onAssessmentTypeChange,
  ]);

  if (isBiosimilar || (readOnly && !progOutcome)) {
    return null;
  }

  const fieldBackground = readOnly ? "#F3F4F6" : "#fff";
  const hoverBorderColor = readOnly ? borderColor : primaryColor;
  const inputCursor = readOnly ? "not-allowed" : "default";
  const assessmentSelectWidth = assessmentSelectRef.current
    ? `${assessmentSelectRef.current.offsetWidth}px`
    : undefined;

  // Consistent keys to sync across state maps
  const keysToUpdate = [
    prog?.id,
    canonicalId,
    prog?.programId,
    prog?.name,
  ].filter(Boolean);

  const updateMapValue = (setter, value) => {
    if (!setter) return;
    setter((prev) => {
      const next = { ...prev };
      keysToUpdate.forEach((k) => {
        if (value === undefined || value === null || value === "") {
          delete next[k];
        } else {
          next[k] = value;
        }
      });
      return next;
    });
  };

  const handleOpenDropdown = (e) => {
    if (readOnly) return;
    setAnchorEl(e.currentTarget);
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
  };

  const handleKeyDown = (e) => {
    if (readOnly) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setAnchorEl(e.currentTarget);
    }
  };

  const resolveOutcomeName = (val) => {
    if (!val) return "";
    const norm = String(val).toUpperCase().trim();
    const matched = referenceData?.outcomes?.find(
      (o) =>
        o.displayName === val ||
        o.outcomeName === val ||
        o.outcome === val ||
        String(o.displayName || "")
          .toUpperCase()
          .trim() === norm ||
        String(o.outcomeName || "")
          .toUpperCase()
          .trim() === norm ||
        String(o.outcome || "")
          .toUpperCase()
          .trim() === norm,
    );
    return matched?.outcomeName || matched?.outcome || matched?.value || val;
  };

  const handleNestedSelect = (parent, child) => {
    const outcomeName = resolveOutcomeName(parent);
    updateMapValue(setSelectedOutcome, outcomeName);
    updateMapValue(setSelectedSubReason, child);

    const isSelectedDeclined = isDeclinedOutcome(outcomeName, referenceData);
    if (!isSelectedDeclined) {
      updateMapValue(setDeclinedReason, "");
      updateMapValue(setDeclinedNotes, "");
    }
    handleOutcomeChangeAssessmentType(outcomeName);
    setAnchorEl(null);
  };

  const handleTypeToggle = (type) => {
    const outcomeName = resolveOutcomeName(type);
    updateMapValue(setSelectedOutcome, outcomeName);
    updateMapValue(setSelectedSubReason, "");

    const isToggledDeclined = isDeclinedOutcome(outcomeName, referenceData);
    if (!isToggledDeclined) {
      updateMapValue(setDeclinedReason, "");
      updateMapValue(setDeclinedNotes, "");
    }
    handleOutcomeChangeAssessmentType(outcomeName);
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        mb: 4,
        p: 2,
        border: `1px solid ${borderColor}`,
        borderRadius: "10px",
        backgroundColor: "#FAFAFD",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ color: primaryColor }}
          >
            {prog?.name || prog?.displayName || prog?.id}
          </Typography>
        </Box>

        {!readOnly && onRemoveProgram && (
          <IconButton
            size="small"
            onClick={() => onRemoveProgram(prog)}
            sx={{
              color: "#9CA3AF",
              p: 0.5,
              "&:hover": {
                color: "#EF4444",
                backgroundColor: "#FEE2E2",
              },
            }}
            title="Remove Program"
          >
            <CloseIcon sx={{ fontSize: "18px" }} />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexWrap: "wrap",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: { xs: 2, sm: 2.5, md: 3 },
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          {/* Program Outcome Selection */}
          <Box
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
              maxWidth: { xs: "100%", sm: "calc(50% - 12px)" },
              minWidth: { xs: "100%", sm: 200 },
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "#403C6F",
                fontWeight: 500,
                mb: 1,
                display: "block",
                letterSpacing: "0.5px",
              }}
            >
              Program Outcome <span style={{ color: "#D32F2F" }}>*</span>
            </Typography>
            <Box
              ref={selectRef}
              role="button"
              tabIndex={readOnly ? -1 : 0}
              onClick={handleOpenDropdown}
              onKeyDown={handleKeyDown}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "40px",
                px: 1.5,
                borderRadius: "8px",
                border: `1px solid ${borderColor}`,
                backgroundColor: fieldBackground,
                cursor: readOnly ? "not-allowed" : "pointer",
                userSelect: "none",
                "&:hover": {
                  borderColor: hoverBorderColor,
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: progOutcome ? "#10100F" : "#9F9DB6",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  userSelect: "none",
                  pointerEvents: "none",
                }}
              >
                {progOutcome ? (
                  <>
                    {getOutcomeDisplayName(progOutcome)}
                    {progSubReason && (
                      <span
                        style={{
                          color: "#403C6F",
                          fontSize: "12px",
                          fontWeight: 400,
                          marginLeft: 4,
                        }}
                      >
                        ({capitalizeFirstLetter(progSubReason)})
                      </span>
                    )}
                  </>
                ) : (
                  "Select program Outcome..."
                )}
              </Typography>
              <KeyboardArrowDownIcon
                sx={{
                  color: "#6B7280",
                  fontSize: "20px",
                  pointerEvents: "none",
                }}
              />
            </Box>
            <CommonSelect
              id={`outcome-select-${prog?.id || canonicalId}`}
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleCloseDropdown}
              filterButtonRef={selectRef}
              horizontal="left"
              noteTypes={outcomesList}
              selectedTypes={
                progOutcome ? [getOutcomeDisplayName(progOutcome)] : []
              }
              multiple={false}
              nestedOptions={
                isFormless ? formlessOutcomeReasons : dynamicOutcomeReasons
              }
              nestedSelectedValue={progSubReason || ""}
              nestedVertical="top"
              onNestedSelect={handleNestedSelect}
              handleTypeToggle={handleTypeToggle}
              width={
                selectRef.current?.offsetWidth
                  ? `${selectRef.current.offsetWidth}px`
                  : undefined
              }
            />
          </Box>

          {/* Assessment Type Selection for non-formless programs */}
          {!isFormless && (
            <Box
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
                maxWidth: { xs: "100%", sm: "calc(50% - 12px)" },
                minWidth: { xs: "100%", sm: 200 },
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#403C6F",
                  fontWeight: 500,
                  mb: 1,
                  display: "block",
                  letterSpacing: "0.5px",
                }}
              >
                Assessment Type
              </Typography>
              <FormControl ref={assessmentSelectRef} fullWidth size="small">
                <Select
                  value={
                    isProgramCompletion
                      ? "DISCHARGE"
                      : normalizeFormType(
                          prog?.formType || prog?.assessmentType,
                        ) || defaultAssessmentType
                  }
                  disabled={isAssessmentTypeDisabled}
                  onChange={(e) => {
                    if (onAssessmentTypeChange) {
                      onAssessmentTypeChange(
                        prog?.id || prog?.programId || canonicalId,
                        e.target.value,
                      );
                    }
                  }}
                  renderValue={(selected) => {
                    const labels = {
                      INITIAL: "Initial Assessment",
                      FOLLOW_UP: "Follow-up Assessment",
                      FOLLOWUP: "Follow-up Assessment",
                      DISCHARGE: "Discharge Assessment",
                    };
                    return (
                      <span style={{ color: "#10100F", fontSize: "13px" }}>
                        {labels[selected] || selected || "Initial Assessment"}
                      </span>
                    );
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: {
                      sx: {
                        backgroundColor: "#6E6D92",
                        color: "#fff",
                        borderRadius: "10px",
                        boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                        mt: 0.5,
                        boxSizing: "border-box",
                        width: assessmentSelectWidth ?? "auto",
                        minWidth: assessmentSelectWidth,
                        maxWidth: assessmentSelectWidth ?? "100%",
                        "& .MuiMenuItem-root": {
                          fontSize: "13px",
                          color: "#fff",
                          py: 1,
                          px: 2,
                          borderRadius: "4px",
                          mx: 0.5,
                          my: 0.25,
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.15)",
                          },
                          "&.Mui-selected": {
                            backgroundColor: "#403C6F !important",
                            fontWeight: 600,
                            "&:hover": {
                              backgroundColor: "#403C6F !important",
                            },
                          },
                        },
                      },
                    },
                  }}
                  sx={{
                    height: "40px",
                    fontSize: "13px",
                    borderRadius: "8px",
                    backgroundColor: fieldBackground,
                    fontWeight: 500,
                    color: "#10100F",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: `${borderColor} !important`,
                      borderWidth: "1px !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: `${hoverBorderColor} !important`,
                      borderWidth: "1px !important",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: `${primaryColor} !important`,
                      borderWidth: "1px !important",
                    },
                    "&.Mui-focused:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: `${primaryColor} !important`,
                      borderWidth: "1px !important",
                    },
                    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                      borderColor: `${borderColor} !important`,
                      borderWidth: "1px !important",
                    },
                    "& .MuiSelect-select": {
                      outline: "none !important",
                      "&:focus": {
                        backgroundColor: "transparent",
                        outline: "none !important",
                      },
                    },
                    "&.Mui-focused": {
                      outline: "none !important",
                      boxShadow: "none !important",
                    },
                  }}
                >
                  <MenuItem value="INITIAL" sx={{ fontSize: "13px" }}>
                    Initial Assessment
                  </MenuItem>
                  <MenuItem value="FOLLOW_UP" sx={{ fontSize: "13px" }}>
                    Follow-up Assessment
                  </MenuItem>
                  <MenuItem value="DISCHARGE" sx={{ fontSize: "13px" }}>
                    Discharge Assessment
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Conditional Reason for Declined */}
          {isDeclined && String(progSubReason).toLowerCase() === "other" && (
            <Box
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
                maxWidth: { xs: "100%", sm: "calc(50% - 12px)" },
                minWidth: { xs: "100%", sm: 200 },
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#403C6F",
                  fontWeight: 500,
                  mb: 1,
                  display: "block",
                  letterSpacing: "0.5px",
                }}
              >
                Reason <span style={{ color: "#D32F2F" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={progReason}
                onChange={(e) =>
                  updateMapValue(setDeclinedReason, e.target.value)
                }
                placeholder="Enter reason for declining..."
                disabled={readOnly}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    "& fieldset": {
                      borderColor: borderColor,
                      borderWidth: "1px !important",
                    },
                    "&:hover fieldset": {
                      borderColor: primaryColor,
                      borderWidth: "1px !important",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: `${primaryColor} !important`,
                      borderWidth: "1px !important",
                    },
                  },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Conditional Declined Notes */}
        {isDeclined && (
          <Box sx={{ width: "100%", mt: 1 }}>
            <Typography
              sx={{
                fontSize: "13px",
                color: "#403C6F",
                fontWeight: 500,
                mb: 1,
                display: "block",
                letterSpacing: "0.5px",
              }}
            >
              Declined Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={progNotes}
              onChange={(e) => updateMapValue(setDeclinedNotes, e.target.value)}
              placeholder="Enter declined notes..."
              disabled={readOnly}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: fieldBackground,
                  cursor: inputCursor,
                  "& fieldset": {
                    borderColor: borderColor,
                    borderWidth: "1px !important",
                  },
                  "&:hover fieldset": {
                    borderColor: hoverBorderColor,
                    borderWidth: "1px !important",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: `${primaryColor} !important`,
                    borderWidth: "1px !important",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#F3F4F6",
                    cursor: "not-allowed",
                    "& fieldset": {
                      borderColor: `${borderColor} !important`,
                      borderWidth: "1px !important",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    cursor: inputCursor,
                  },
                  "& .MuiOutlinedInput-input.Mui-disabled": {
                    color: "#10100F !important",
                    WebkitTextFillColor: "#10100F !important",
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

EnrolledProgram.propTypes = {
  prog: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
  primaryColor: PropTypes.string,
  borderColor: PropTypes.string,
  onRemoveProgram: PropTypes.func,
  onAssessmentTypeChange: PropTypes.func,
  selectedOutcome: PropTypes.object,
  setSelectedOutcome: PropTypes.func,
  selectedSubReason: PropTypes.object,
  setSelectedSubReason: PropTypes.func,
  declinedReason: PropTypes.object,
  setDeclinedReason: PropTypes.func,
  declinedNotes: PropTypes.object,
  setDeclinedNotes: PropTypes.func,
  referenceData: PropTypes.object,
  outcomesList: PropTypes.array,
  formlessOutcomeReasons: PropTypes.object,
  dynamicOutcomeReasons: PropTypes.object,
  getOutcomeDisplayName: PropTypes.func,
};

export default EnrolledProgram;
